/**
 * Room.js
 * 
 * This is the main AuctionRoom
 * 
 * Has different pages for the people to view.
 * 1. Select Room page (sessionID = ''); users can start a new room.
 * 2. 
 * 
 * (Future) update this for people to have their own lists and contraints and bonus points
 * Make this into a complete game
 */
import React, { useEffect, useState } from "react";
import { useClientAuth } from "../../../hooks/clientAuth";
import { AuctionRoomsCollection } from "../../../assets/DatabaseCollections";
import TeamSelectionPage from "./TeamSelectionPage";
import TeamStatus from "./TeamStatus";
import AdminScreen from "./AdminScreen";
import { useParams } from "react-router-dom";
import { subscribeToRoom } from "../../../Rooms/helpers/joinRoom";
import { subscribeToTeams } from "../../teamHelpers";
import AuctionLobby from "./AuctionLobby";
import SoldAnnouncement, { useSaleAnnouncement } from "../Cards/SoldAnnouncement";
import { getRoomLibrary } from "../library/libraries";
import AuctionBreak from "./AuctionBreak";
import AuctionResults from "./AuctionResults";
import SubmitItems from "./SubmitItems";
import SubmissionProgress from "./SubmissionProgress";
import { getAuctionSettings } from "../assets/Rules";
import { isRoomInLobby, isRoomSubmitting } from "../helpers/auctionLobby";
import ExitRoom from "../../../components/Basic/ExitRoom";
import RoomCodeBadge from "../../../Rooms/components/RoomCodeBadge";
import LoadingScreen from "../../../components/Basic/LoadingScreen";

const AuctionRoom = () => {
    const [liveRoom, setRoom] = useState({});
    const [isRoomLoaded, setIsRoomLoaded] = useState(false);
    const [userTeam, setUserTeam] = useState(null);
    const [teams, setTeams] = useState([]);
    const [teamsLoadedFor, setTeamsLoadedFor] = useState(null);
    const [dbCollection, ] = useState(AuctionRoomsCollection)
    const { user, loading: isUserLoading } = useClientAuth();
    const roomID = useParams();
    const announcement = useSaleAnnouncement(liveRoom.lastSale);


    // Live room updates, so everyone moves on when the host starts the auction
    useEffect(() => {
        if (!roomID.room) return;
        return subscribeToRoom(dbCollection, roomID.room, (latestRoom) => {
            setRoom(latestRoom || {});
            setIsRoomLoaded(true);
        });
    }, [dbCollection, roomID.room]);

    // Worked out on render (not in an effect), so the host never sees the player screens first
    const isAdmin = !!user && (user.uid === liveRoom.admin?.userID || user.uid === liveRoom.createdBy?.userID);


    // Live team updates (players joining, items bought, budgets)
    useEffect(() => {
        if (!liveRoom.id) return;
        return subscribeToTeams(dbCollection, liveRoom.id, (latestTeams) => {
            setTeams(latestTeams);
            setTeamsLoadedFor(liveRoom.id);
        });
    }, [dbCollection, liveRoom.id]);

    // While a SOLD flash is on screen, keep showing the sold item (and hold off any break or the results),
    // so the next screen only appears once the flash has faded out
    const heldSale = announcement.heldSale;
    const heldItemIndex = heldSale
        ? heldSale.itemIndex ?? getRoomLibrary(liveRoom).items.findIndex((item) => item.name === heldSale.movieName)
        : -1;
    const room = heldItemIndex >= 0
        ? { ...liveRoom, currentMovieIndex: heldItemIndex, onBreak: false, status: ["finished", "submitting"].includes(liveRoom.status) ? "live" : liveRoom.status }
        : liveRoom;

    // The user's team, kept in sync with the live teams (found by membership too, so returning players skip team selection)
    const currentUserTeam =
        teams.find((team) => team.id === userTeam?.id) ||
        teams.find((team) => team.members?.some((member) => (member.userID || member.uid) === user?.uid)) ||
        userTeam;
    const areTeamsLoaded = !!liveRoom.id && teamsLoadedFor === liveRoom.id;
    const inLobby = isRoomInLobby(room);
    const isFinished = room.status === "finished";
    const isSubmitting = isRoomSubmitting(room);
    const onBreak = !inLobby && !isFinished && !isSubmitting && !!room.onBreak;
    const settings = getAuctionSettings(room);


    if (isUserLoading) return <LoadingScreen message="Signing you in" />;

    // User check (placeholder; replace with your actual auth context)
    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black text-zinc-100">
                <p className="text-lg font-medium">Please login first to continue.</p>
            </div>
        );
    }

    if (!isRoomLoaded) return <LoadingScreen message="Joining the room" />;
    if (liveRoom.id && !areTeamsLoaded) return <LoadingScreen message="Loading teams" />;

    return (
        <div className={`min-h-screen flex flex-col items-center justify-center ${userTeam? 'bg-black' : 'bg-black'}`}>
            {room.id ? (
                <>
                    {!inLobby && !onBreak && !isFinished && !isSubmitting && <RoomCodeBadge roomID={room.id} />}
                    <SoldAnnouncement
                        sale={announcement.sale}
                        showSales={settings.showSales}
                        onDismiss={announcement.dismiss}
                        onFaded={announcement.release}
                    />
                    {isFinished ? (
                        <AuctionResults room={room} teams={teams} isAdmin={isAdmin} user={user} userTeam={currentUserTeam} />
                    ) : isSubmitting ? (
                        isAdmin
                            ? <SubmissionProgress room={room} teams={teams} />
                            : currentUserTeam
                                ? <SubmitItems key={currentUserTeam.id} room={room} team={currentUserTeam} />
                                : <div className="text-zinc-300 text-xl px-6 text-center">The auction is over. Teams are choosing what to submit.</div>
                    ) : isAdmin ? (
                        inLobby
                            ? <AuctionLobby room={room} teams={teams} />
                            : onBreak
                                ? <AuctionBreak room={room} teams={teams} isAdmin />
                                : <AdminScreen room={room} teams={teams} />
                    ) : currentUserTeam && onBreak ? (
                        <AuctionBreak room={room} teams={teams} userTeam={currentUserTeam} />
                    ) : currentUserTeam ? (
                        <>
                            {inLobby && (
                                <div className="w-full max-w-5xl px-6 mt-24 -mb-12">
                                    <div className="flex items-center gap-3 rounded-2xl border border-yellow/40 bg-yellow/10 px-5 py-4 text-zinc-100">
                                        <span className="w-2 h-2 rounded-full bg-yellow animate-pulse" />
                                        Waiting for {room.admin?.displayName || "the host"} to start the auction
                                    </div>
                                </div>
                            )}
                            <TeamStatus team={currentUserTeam} allTeams={teams} room={room} />
                        </>
                    ) : (
                        <TeamSelectionPage
                            user={user}
                            room={room}
                            dbCollection={dbCollection}
                            teams={teams}
                            setUserTeam={setUserTeam}
                        />
                    )}
                    <ExitRoom/>
                </>
            ) : (
                <div className="text-zinc-100 text-lg">
                    Room Unavailable
                </div>
            )}
        </div>
    );
};

export default AuctionRoom;